import type { ScheduleRow } from './useScheduleQuery.js';

/**
 * Bucketing appointments into the week board's day columns.
 *
 * ⚠ THE COLUMN IS THE LOCAL DAY, NOT THE UTC DAY. Appointment `start` is an instant; the office
 * reads the board in its own timezone. Bucketing on the UTC date puts an 8pm appointment in the
 * NEXT day's column east of UTC-0 — the classic off-by-one that only shows up in the evening and
 * only for some rows.
 *
 * ⚠ AND THAT IS WHY THE FETCH IS WIDER THAN THE WEEK. The server window is UTC-bounded
 * (`startsOnOrAfter` / `startsBefore` take instants), so a UTC Monday-to-Monday window in EDT
 * actually spans Sunday 8pm to Sunday 8pm local — it would MISS Sunday evening and include the
 * previous Sunday evening. `fetchWindowFor` pads a day either side and the bucketing below discards
 * whatever falls outside the seven local days. Padding is cheap; a silently absent Sunday is not.
 */

/** Local YYYY-MM-DD for an instant. Deliberately not `toISOString`, which is UTC. */
export function localDayKey(iso: string): string | null {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const d = new Date(t);
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * The window to ask the server for, given the seven local days on screen.
 *
 * `to` is EXCLUSIVE (it maps to ServiceTitan's `startsBefore`), and both ends are padded by a day
 * so no local-time row can fall outside the UTC-bounded upstream query.
 */
export function fetchWindowFor(days: string[]): { from: string; to: string } {
  const first = days[0] ?? '';
  const last = days.at(-1) ?? first;
  return { from: shiftIsoDate(first, -1), to: shiftIsoDate(last, 2) };
}

/** YYYY-MM-DD shifted by whole days, computed on a UTC anchor so DST cannot skew it. */
function shiftIsoDate(iso: string, delta: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

/**
 * Rows grouped into the given day columns, each column ordered by start time.
 *
 * Rows outside the seven days are dropped — they only exist because of the padded fetch. A row with
 * an unparseable start is dropped from the BOARD (there is no column it belongs in) but the caller
 * still has it in `rows`, so it can be surfaced separately rather than silently lost.
 */
export function bucketByDay(
  rows: ScheduleRow[],
  days: string[],
): { columns: Map<string, ScheduleRow[]>; undated: ScheduleRow[] } {
  const columns = new Map<string, ScheduleRow[]>(days.map((d) => [d, []]));
  const undated: ScheduleRow[] = [];

  for (const row of rows ?? []) {
    const key = localDayKey(row.start);
    if (key === null) {
      undated.push(row);
      continue;
    }
    const column = columns.get(key);
    if (column) column.push(row);
  }

  for (const column of columns.values()) {
    column.sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  }

  return { columns, undated };
}
